import { Link } from 'react-router-dom';
import { useLanguage } from '@/lib/i18n';
import { useSameRouteScroll } from '@/lib/sameRouteScroll';
import { ShieldCheck, Mail, MapPin, Phone, Clock, FileText, MessageSquare, Facebook, Instagram } from 'lucide-react';
import OpenContactFormButton from '@/components/OpenContactFormButton';
import { SITE_NAV_SECTIONS, resolveNavPath } from '@/data/siteNav';

/* Per-item Link so we can call useSameRouteScroll once per row.
   When the user clicks a footer link for the page they're already on,
   we smooth-scroll to top instead of letting React Router silently no-op. */
function FooterLink({ to, className, testId, external, children }) {
  const onClick = useSameRouteScroll(to);
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" className={className} data-testid={testId}>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} onClick={onClick} className={className} data-testid={testId}>
      {children}
    </Link>
  );
}

export default function Footer() {
  const { lang } = useLanguage();
  const p = `/${lang}`;
  const ja = lang === 'ja';

  // Footer columns are DERIVED from the canonical SITE_NAV_SECTIONS so
  // the mobile hamburger menu, the mobile footer accordion, and the
  // desktop footer grid all show the EXACT same comprehensive page
  // list. When a new page is added to siteNav.js, it appears in all
  // three places automatically — no three-file sync dance.
  const columns = SITE_NAV_SECTIONS.map((sec) => ({
    key: sec.key,
    title: sec.title[lang] || sec.title.en,
    desc: (sec.subtitle && (sec.subtitle[lang] || sec.subtitle.en)) || '',
    items: sec.items.map((it) => {
      const isExternal = it.external || (it.path || '').startsWith('http');
      return {
        label: it.label[lang] || it.label.en,
        path: isExternal ? it.path : resolveNavPath(it.path, lang),
        external: isExternal,
        highlight: it.highlight,
      };
    }),
  }));

  return (
    <footer className="bg-slate-900 text-white" data-testid="main-footer" data-print-hide="true" data-safe-area-bottom>
      {/* ── Brand row — establishes trust before the link grid ── */}
      <div className="border-b border-white/5">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-7">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 md:gap-10 items-start md:items-center">
            <div>
              <p className="text-base sm:text-lg font-bold text-white tracking-tight">
                {ja ? 'Japan Toll Receipts' : 'Japan Toll Receipts'}
                <span className="ml-2 text-[10px] font-mono font-bold uppercase tracking-[0.2em] text-emerald-400/80 align-middle">
                  {ja ? 'NoJo Enterprise運営' : 'By NoJo Enterprise'}
                </span>
              </p>
              <p className="text-[11px] text-gray-400 mt-1 max-w-xl leading-relaxed">
                {ja
                  ? 'ETC利用照会サービス（MEISAI）連携のメール自動配信。ETC利用明細・ETC領収書・ETC利用証明書を日次・週次・月次でお届け。運営会社のNoJo Enterpriseは2013年から日本でETC関連サービスを提供しています（独立運営のサービスです）。'
                  : 'ETC Usage Inquiry Service (MEISAI) email reports. Automated ETC toll usage statements, receipts, and usage certificates delivered daily, weekly, or monthly. An independent service operated by NoJo Enterprise, which has provided ETC-related services in Japan since 2013.'}
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] font-semibold text-gray-300">
                <ShieldCheck className="w-3 h-3 text-emerald-400" strokeWidth={2.5} />
                {ja ? 'パススルー認証' : 'Pass-through'}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] font-semibold text-gray-300">
                <FileText className="w-3 h-3 text-purple-400" strokeWidth={2.5} />
                {ja ? '31項目チェック' : '31-check validator'}
              </span>
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white/5 border border-white/10 text-[10px] font-semibold text-gray-300">
                <ShieldCheck className="w-3 h-3 text-amber-400" strokeWidth={2.5} />
                {ja ? '個人情報を記録しません' : 'Zero-PII logs'}
              </span>
              <a
                href="https://www.facebook.com/JapanTollReceipts"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-[#1877F2] hover:bg-[#0f63d1] text-[10px] font-bold text-white transition-colors"
                data-testid="footer-facebook-link"
                aria-label={ja ? 'Facebookページを開く（新しいタブ）' : 'Open our Facebook page (new tab)'}
              >
                <Facebook className="w-3 h-3" strokeWidth={2.5} />
                {ja ? 'Facebookでいいね！' : 'Like us on Facebook'}
              </a>
              <a
                href="https://www.instagram.com/japantollreceipts/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-gradient-to-tr from-[#feda75] via-[#d62976] to-[#4f5bd5] hover:opacity-90 text-[10px] font-bold text-white transition-opacity"
                data-testid="footer-instagram-link"
                aria-label={ja ? 'Instagramを開く（新しいタブ）' : 'Open our Instagram (new tab)'}
              >
                <Instagram className="w-3 h-3" strokeWidth={2.5} />
                {ja ? 'Instagramでフォロー' : 'Follow us on Instagram'}
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Footer link grid ── */}
      {/* Mobile: collapsible accordions (one per column, all collapsed
          by default except the first). Desktop (≥ sm): the existing
          4-column grid. Same `columns` data drives both. */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Mobile accordion (< sm) — Nojo Enterprise / JapanETCcard pattern */}
        <div className="block sm:hidden space-y-2" data-testid="footer-mobile-accordion">
          {columns.map((col, ci) => (
            <details
              key={col.key}
              open={ci === 0}
              className="group rounded-xl border border-white/10 bg-white/5 overflow-hidden"
              data-testid={`footer-mobile-col-${col.key}`}
            >
              <summary className="flex items-center justify-between px-4 py-3 cursor-pointer list-none">
                <span className="flex flex-col min-w-0">
                  <span className="text-[12px] font-bold uppercase tracking-[0.18em] text-white truncate">
                    {col.title}
                  </span>
                  {col.desc && (
                    <span className="text-[10px] text-gray-400 italic truncate">{col.desc}</span>
                  )}
                </span>
                <span className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-[10px] text-gray-500 tabular-nums">{col.items.length}</span>
                  <svg className="w-4 h-4 text-gray-400 group-open:rotate-180 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="6 9 12 15 18 9" /></svg>
                </span>
              </summary>
              <ul className="px-4 pb-3 space-y-1.5 border-t border-white/5">
                {col.items.map((item) => (
                  <li key={item.path + item.label} className="pt-2">
                    <FooterLink
                      to={item.path}
                      external={item.external}
                      className={`text-[13px] leading-snug transition-colors block ${
                        item.highlight === 'green'
                          ? 'text-[#7dd35e] hover:text-emerald-300 font-semibold'
                          : item.highlight === 'purple'
                            ? 'text-[#c993e0] hover:text-purple-200 font-semibold'
                            : item.highlight === 'blue' || item.highlight === 'amber'
                              ? 'text-[#7ac6ff] hover:text-sky-200 font-semibold'
                              : 'text-gray-300 hover:text-white'
                      }`}
                      testId={`footer-link-mobile-${col.key}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {item.label}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </details>
          ))}
        </div>

        {/* Desktop grid (≥ sm) — 11 sections fit naturally in 3 columns at md+ */}
        <div className="hidden sm:grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-9">
          {columns.map((col) => (
            <div key={col.key} data-testid={`footer-col-${col.key}`}>
              <h4 className="text-[10px] font-bold uppercase tracking-[0.18em] text-white border-b border-white/10 pb-2 mb-1">
                {col.title}
              </h4>
              <p className="text-[10px] text-gray-500 italic mb-3">{col.desc}</p>
              <ul className="space-y-1.5">
                {col.items.map((item) => (
                  <li key={item.path + item.label}>
                    <FooterLink
                      to={item.path}
                      external={item.external}
                      className={`text-xs leading-snug transition-colors block ${
                        item.highlight === 'green'
                          ? 'text-[#7dd35e] hover:text-emerald-300 font-semibold'
                          : item.highlight === 'purple'
                            ? 'text-[#c993e0] hover:text-purple-200 font-semibold'
                            : item.highlight === 'blue' || item.highlight === 'amber'
                              ? 'text-[#7ac6ff] hover:text-sky-200 font-semibold'
                              : 'text-gray-400 hover:text-white'
                      }`}
                      testId={`footer-link-${col.key}-${item.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                    >
                      {item.label}
                    </FooterLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* ── Contact strip — phone, email, address, hours ── */}
      <div className="border-t border-white/5 bg-black/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-[11px]">
            <div className="flex items-start gap-2">
              <Phone className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">{ja ? '電話' : 'Phone'}</div>
                <div className="text-gray-300">046-874-7156</div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MessageSquare className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">{ja ? 'お問い合わせ' : 'Contact'}</div>
                <OpenContactFormButton
                  variant="ghost"
                  sourcePage="footer"
                  showIcon={false}
                  data-testid="footer-open-contact-form"
                >
                  {ja ? 'お問い合わせ' : 'Contact us'}
                </OpenContactFormButton>
                <div className="text-[10px] text-gray-500 leading-snug mt-0.5">
                  {ja ? 'チームが直接ご返信します' : 'A real person on our team replies'}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <MapPin className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">{ja ? '所在地' : 'Office'}</div>
                <Link to={`${p}/contact#our-location`} className="text-gray-300 hover:text-white">
                  {ja ? '横須賀・神奈川' : 'Yokosuka, Kanagawa'}
                </Link>
                <div className="text-[10px] text-gray-500 leading-snug mt-0.5">
                  {ja ? '住所と地図を見る →' : 'Full address & map →'}
                </div>
              </div>
            </div>
            <div className="flex items-start gap-2">
              <Clock className="w-3.5 h-3.5 text-gray-500 mt-0.5 flex-shrink-0" strokeWidth={2} />
              <div>
                <div className="text-[9px] font-bold uppercase tracking-wider text-gray-500 mb-0.5">{ja ? '営業時間' : 'Hours'}</div>
                <div className="text-gray-300">{ja ? '月〜金 9:00–17:00 JST' : 'Mon–Fri 9:00–17:00 JST'}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Legal foot row ── */}
      <div className="border-t border-white/5">
        {/* `pb-14` on mobile leaves room for the StickySupport bottom
            bar (h-12 = 48px) so the copyright line and policy links
            stay tappable above the floating support / try-free / chat
            row. Desktop uses `pb-4` since the bar is `lg:hidden`. */}
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 pb-14 lg:pb-4">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 mb-3">
            <div className="flex flex-col gap-1">
              <p className="text-[10px] text-gray-500">
                &copy; 2013–{new Date().getFullYear()} NoJo Enterprise, Co. Ltd. {ja ? 'All Rights Reserved.' : 'All rights reserved.'}
              </p>
              <a
                href="https://www.nojoenterprise.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-[10px] text-gray-400 hover:text-white transition-colors"
                data-testid="footer-nojo-corporate-link"
                aria-label={ja ? 'ノージョエンタープライズ株式会社のサイトを開く' : 'Open NoJo Enterprise corporate website (new tab)'}
              >
                <img
                  src="/brand/nojoenterprise-logo.png"
                  alt=""
                  aria-hidden="true"
                  className="w-3.5 h-3.5 object-contain"
                  width={14}
                  height={14}
                />
                <span className="font-medium">www.NoJoEnterprise.com</span>
                <span className="text-gray-600">↗</span>
              </a>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <Link to={`${p}/policies`} className="text-[10px] text-gray-500 hover:text-white">{ja ? '規約・手続き' : 'Policies & Procedures'}</Link>
              <span className="text-gray-700">·</span>
              <Link to="/privacy" className="text-[10px] text-gray-500 hover:text-white">{ja ? 'プライバシーポリシー' : 'Privacy'}</Link>
              <span className="text-gray-700">·</span>
              <Link to="/terms" className="text-[10px] text-gray-500 hover:text-white">{ja ? '利用規約' : 'Terms'}</Link>
              <span className="text-gray-700">·</span>
              <Link to="/legal/commercial-transactions" className="text-[10px] text-gray-500 hover:text-white">{ja ? '特定商取引法に基づく表記' : 'Commercial Transactions'}</Link>
              <span className="text-gray-700">·</span>
              <Link to="/sitemap" className="text-[10px] text-gray-500 hover:text-white">{ja ? 'サイトマップ' : 'Sitemap'}</Link>
              <span className="text-gray-700">·</span>
              <Link to={`${p}/architecture`} className="text-[10px] text-gray-500 hover:text-white" data-testid="footer-architecture">{ja ? 'システム構成' : 'Architecture'}</Link>
              <span className="text-gray-700">·</span>
              <Link to="/email/preferences" className="text-[10px] text-gray-500 hover:text-white" data-testid="footer-email-preferences">{ja ? 'メール配信設定・解除' : 'Email Preferences / Unsubscribe'}</Link>
            </div>
          </div>
          <p className="text-[10px] text-gray-500 leading-relaxed text-center max-w-3xl mx-auto">
            {ja
              ? 'Japan Toll Receiptsは独立運営のレポート自動化サービスです。ETC利用照会サービス、NEXCO、道路事業者とは関係がありません。通行料金の収納・徴収は行いません。'
              : 'Japan Toll Receipts is an independent report automation service. We are not the official ETC inquiry service, NEXCO, or a road operator. We do not collect toll payments.'}
          </p>
        </div>
      </div>
    </footer>
  );
}
